import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * UI-54 (operator-ruled 2026-08-11) — render a dialog into `document.body` so it escapes
 * whatever stacking context its owner sits in.
 *
 * WHY THIS EXISTS. `pages/DirectoryPage.tsx` mounts its whole subtree in a positioned,
 * z-indexed container (`fixed … z-10` while visible). That creates a STACKING CONTEXT, and
 * a z-index inside it — however large — can only order against siblings *within* it. So
 * FilterModal's `z-[1100]` never outranked the header's `z-50`: the header is a sibling of
 * the directory in the ROOT context, and the whole directory loses to it as one unit.
 * Two dialogs were caught by that one cause:
 *   · the filter panel, whose backdrop also started 64px down, leaving three header
 *     controls lit and tappable — and the hamburger opens the nav drawer (z-[60]/z-[70],
 *     a root-context sibling) straight over the "modal";
 *   · the save-search box (`z-[100]`), confined by exactly the same container.
 * Geometry alone could not fix either: widening the backdrop leaves the paint order wrong.
 *
 * ⚠️ FIRST USE OF createPortal IN THIS REPO — there were zero call sites before this
 * (control: `react-dom` was imported only by three test files). Two consequences:
 *
 * 1. SSR/PRERENDER SAFETY. This site is statically prerendered by vite-react-ssg in Node,
 *    where `document` does not exist. The mounted guard below means the portal renders
 *    NOTHING on the server and on the hydration render, then attaches on the client.
 *    That is safe here because both consumers are `isOpen`-gated and closed during a
 *    build, so they emit no markup either way — but the guard must stay, because a future
 *    consumer that is open at first render would otherwise crash the build.
 * 2. IT DOES NOT SUPPRESS RE-RENDERS. Portalled children remain part of the React tree
 *    for context and events; only the DOM position changes. Focus traps, escape handlers
 *    and context consumers keep working exactly as before.
 *
 * SCOPE: this component does one thing — relocate the DOM node. It deliberately does NOT
 * add a backdrop, focus trap, scroll lock or aria wiring; each consumer already owns those
 * and duplicating them here would create two sources of truth for modal behaviour.
 */
export const ModalPortal = ({ children }: { children: ReactNode }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted || typeof document === 'undefined') return null;
    return createPortal(children, document.body);
};
